import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import {
  IncomeOptionsDTO,
  IncomeType,
} from '../../../../../../../ProfileOPL/Income/api/useIncomeQuery';
import { EmploymentFormFields } from '../../../../../../../ProfileOPL/Income/forms/Employment/EmploymentFormFields';
import { RealEstateFormFields } from '../../../../../../../ProfileOPL/Income/forms/RealEstate/RealEstateFormFields';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { DEFAULT_INCOME, FormValues } from './IncomeChange.meta';
import { getShowAdvanced } from '../../../../../../../ProfileOPL/Income/Income.meta';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';

interface IncomeProps {
  options: IncomeOptionsDTO;
}

const FormsDict = {
  [IncomeType.Salary]: EmploymentFormFields,
  [IncomeType.RealEstate]: RealEstateFormFields,
};

export const IncomeChange = ({ options }: IncomeProps) => {
  const [, { has: isIncomeCollapsed, toggle: toggleIncome }] = useSet<string | null>();

  const formContext = useFormContext<FormValues>();

  const { control, watch } = formContext;

  const { fields, append: appendIncome, remove: removeIncome } = useFieldArray({
    control,
    name: 'Incomes',
  });

  const watchIncomes = watch('Incomes');

  const incomes = fields.map((field, index) => ({
    ...field,
    ...watchIncomes[index],
  }));

  const incomeTypeOptions = options.IncomeTypeOptions.filter(({ value }) => {
    if (value === IncomeType.Salary) {
      return true;
    } else if (value === IncomeType.RealEstate) {
      return options.RealEstateAssetOptions.length > 0;
    }
    return false;
  });

  return (
    <>
      <Accordion isActive={false} css="border: none; border-radius: 0; ">
        {incomes.map((income, index) => {
          const buttonId = `what-if-income--accordion-button--${income.id}`;
          const panelId = `what-if-income--accordion-panel--${income.id}`;
          const IncomeFormFields =
            income.IncomeType != null && income.IncomeType in FormsDict
              ? FormsDict[income.IncomeType as keyof typeof FormsDict]
              : undefined;

          const defaultShowAdvanced = getShowAdvanced(income);

          return (
            <React.Fragment key={income.id}>
              <AccordionButton
                id={buttonId}
                panelId={panelId}
                isActive={!isIncomeCollapsed(income.id)}
                onClick={() => toggleIncome(income.id)}
                extra={
                  !income.IsBasePlanIncome && (
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => removeIncome(index)}
                    />
                  )
                }
              >
                {!!income.Name ? income.Name : `Income ${index + 1}`}
              </AccordionButton>

              <StyledAccordionPanel
                id={panelId}
                buttonId={buttonId}
                isActive={!isIncomeCollapsed(income.id)}
              >
                <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
                  <Controller
                    name={`Incomes.${index}.IncomeType`}
                    control={control}
                    render={({ field: { value: _val, onChange } }) => (
                      <Select
                        label="Income Type"
                        placeholder="Select Income Type"
                        value={incomeTypeOptions.find(({ value }) => value === income.IncomeType)}
                        options={incomeTypeOptions}
                        onChange={option => onChange(option ? option.value : null)}
                        isPortal
                      />
                    )}
                  />

                  {IncomeFormFields && (
                    <IncomeFormFields
                      formBag={formContext}
                      options={options}
                      fieldNamePrefix={`Incomes.${index}.`}
                      defaultShowAdvanced={defaultShowAdvanced}
                    />
                  )}
                </Box>
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>

      <Button
        variant="link"
        leftIcon="fas fa-plus"
        onClick={() => appendIncome({ ...DEFAULT_INCOME, Earner: options.OwnerOptions[0] })}
        my="24"
        ml="16"
      >
        {`Add ${incomes.length > 0 ? 'another ' : ''}income`}
      </Button>
    </>
  );
};
